import { useDispatch } from 'react-redux';
import { StepsResult } from 'enums/FlowNextResults';
import { RoutePath } from 'enums/Routes';
import { useNavigate } from 'hooks/useNavigate';
import { IncomeVerificationType } from 'components/Verification/verificationSteps';

export type StepId = string;
export type StepProgress = Record<StepId, boolean>;

export interface StepComponent {
  handleNext(result?: any, freshProgress?: Readonly<StepProgress>): void;
}

export interface StepMeta {
  type: IncomeVerificationType;
}

export interface StepData<Id extends StepId> {
  id: Id;
  name: string;
  component: React.FunctionComponent<StepComponent>;
  icon: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  meta?: StepMeta;
}

export const getStepById = <Id extends StepId>(steps: Readonly<StepData<Id>[]>, stepId: Id) =>
  steps.find(({ id }) => id === stepId) ?? steps[0];

export const getNextStep = <Id extends StepId>(
  stepsStatus: StepProgress,
  currentStep: Id,
  steps: Readonly<StepData<Id>[]>,
): Id => {
  const currentIndex = steps.findIndex((step) => step.id === currentStep);
  const nextStepsList = steps.slice(currentIndex + 1);

  let nextStep = nextStepsList.find((step) => !stepsStatus[step.id]);

  if (!nextStep) {
    nextStep = steps.find((step) => !stepsStatus[step.id]);
  }

  if (!nextStep) {
    // All steps are completed.
    nextStep = steps[currentIndex + 1];
  }

  return nextStep?.id ?? steps[0].id;
};

type StepAction<Id extends StepId> = (payload: Id) => { payload: Id; type: string };

export type UseStepsReturnType<Id extends StepId, Progress extends StepProgress> = {
  currentStep: Id;
  isLastStep: boolean;
  handleNextStep: (result?: StepsResult, freshProgress?: Readonly<Progress>) => void;
  setCurrentStep: (selectedStep: Id) => void;
  firstUncompletedStep: Id;
  firstIncompleteStepIndex: number;
  stepsStarted: boolean;
  stepsProgress: Readonly<Progress>;
  stepsCompleted: boolean;
  stepData: Readonly<StepData<Id>[]>;
};

export const useSteps = <Id extends StepId, Progress extends StepProgress>(
  currentStep: Id,
  setCurrentStepAction: StepAction<Id>,
  completeStepAction: StepAction<Id>,
  steps: Readonly<StepData<Id>[]>,
  progress: Readonly<Progress>,
  nextRoute: RoutePath,
): UseStepsReturnType<Id, Progress> => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const stepsUncompleted = steps.filter((step) => !progress[step.id]);
  const stepsCompleted = steps.filter((step) => progress[step.id]);

  const firstUncompletedStep: Id = stepsUncompleted[0]?.id ?? steps[0].id;
  const firstIncompleteStepIndex = steps.findIndex((step) => !progress[step.id]);
  const isLastStep = stepsCompleted.length === steps.length - 1;
  const stepsStarted = stepsCompleted.length >= 1;
  const stepsAllCompleted = stepsCompleted.length === steps.length;

  const setCurrentStep = (selectedStep: Id) => {
    dispatch(setCurrentStepAction(selectedStep));
  };

  const completeStep = (stepToComplete: Id) => {
    dispatch(completeStepAction(stepToComplete));
  };

  const handleNext = () => {
    navigate(nextRoute, { replace: true });
  };

  const handleNextStep = (result?: StepsResult, freshProgress?: Readonly<Progress>) => {
    const shouldCompleteCurrentStep = Boolean(result);
    const progressWithCurrentStepCompleted = {
      ...(freshProgress ?? progress),
      [currentStep]: true,
    } as Progress;
    const effectiveProgress = shouldCompleteCurrentStep ? progressWithCurrentStepCompleted : freshProgress ?? progress;
    const nextStep = getNextStep(effectiveProgress, currentStep, steps);
    const remainingUncompletedSteps = steps.filter((step) => !effectiveProgress[step.id]);

    if (result && remainingUncompletedSteps.length === 0) {
      return handleNext();
    }

    if (result) {
      analytics.track(`${getStepById(steps, currentStep)?.name} Completed`);
      completeStep(currentStep);
    }

    setCurrentStep(nextStep);

    analytics.track(`${getStepById(steps, nextStep)?.name} Entered`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return {
    currentStep,
    isLastStep,
    handleNextStep,
    setCurrentStep,
    firstUncompletedStep,
    firstIncompleteStepIndex,
    stepsStarted,
    stepsProgress: progress,
    stepsCompleted: stepsAllCompleted,
    stepData: steps,
  };
};

export type StepsHook<Id extends StepId, Progress extends StepProgress> = () => UseStepsReturnType<Id, Progress>;
